import React, { useMemo } from 'react';
import { useCallback } from 'react';

export type UseCrudOperationsPayload<T extends Record<string, unknown>> = {
  onAdd: (item: T) => void;
  onEdit: (index: number, item: Partial<T>) => void;
  onDelete: (index: number) => void;
};

export const useCrudOperations = <T extends Record<string, unknown>>({
  items,
  setItems,
  postProcessNewItems,
}: {
  items: T[];
  setItems: (items: T[]) => void;
  postProcessNewItems?: (newItems: T[]) => T[];
}): UseCrudOperationsPayload<T> => {
  const onAdd = useCallback(
    (item: T) => {
      const newItems = [...items, item];
      if (postProcessNewItems) {
        const processedItems = postProcessNewItems(newItems);
        setItems(processedItems);
        return;
      } else {
        setItems(newItems);
        return;
      }
    },
    [items, postProcessNewItems, setItems],
  );

  const onEdit = useCallback(
    (index: number, item: Partial<T>) => {
      const newItems = items.map((currentItem, currentIndex) =>
        currentIndex === index ? { ...currentItem, ...item } : currentItem,
      );

      if (postProcessNewItems) {
        const processedItems = postProcessNewItems(newItems);
        setItems(processedItems);
        return;
      } else {
        setItems(newItems);
        return;
      }
    },
    [items, postProcessNewItems, setItems],
  );

  const onDelete = useCallback(
    (index: number) => {
      const newItems = items.filter(
        (_, currentIndex) => currentIndex !== index,
      );

      if (postProcessNewItems) {
        const processedItems = postProcessNewItems(newItems);
        setItems(processedItems);
        return;
      } else {
        setItems(newItems);
        return;
      }
    },
    [items, postProcessNewItems, setItems],
  );

  return useMemo(() => {
    return {
      onAdd,
      onEdit,
      onDelete,
    };
  }, [onAdd, onEdit, onDelete]);
};
